import React from 'react';
import {
  Button, IconButton, Textarea, Badge, Skeleton, SkeletonRegion, Alert, ToggleGroup, Select,
} from '@parsina/framework';
import {
  ArrowRight, MapPin, Square, Circle, MoveUpRight, PenLine, MousePointer2,
  Trash2, ZoomIn, ZoomOut, Maximize2, Sun, Contrast, Sparkles, Check, RotateCcw,
  Tag,
} from 'lucide-react';
import { C, CT, soft } from '../../constants/theme';
import { VIEW_TYPES } from '../../lib/imageMarks.js';
import { LTR_TEXT, MIRROR_ICON } from '../../i18n/I18nProvider.jsx';
import {
  MARK_PIN, MARK_BOX, MARK_ELLIPSE, MARK_ARROW, MARK_FREEHAND,
  MARK_COLORS, DEFAULT_MARK_COLOR, MAX_MARKS, MAX_COMMENT_CHARS, MAX_STROKE_POINTS,
  DRAGGED_KINDS, SIZED_KINDS, toUnit, toPercent, geometryFor, isDragMeaningful,
  newMark, strokePoints, anchorOf, isLocalized, confidenceTone, isConfirmable,
} from '../../lib/imageMarks.js';

// جلسهٔ درمان → فایل‌ها → one image, opened.
//
// ────────────────────────────────────────────────────────────────────────────
// WHAT THIS SCREEN IS FOR
// ────────────────────────────────────────────────────────────────────────────
// A dentist looking at a periapical does three things, in this order: they LOOK
// (zoom, and push the contrast, because that is how a film is read), they MARK
// what they found so the next person sees it, and they read what the assistant
// thinks it saw. The layout follows that order — the image is the whole screen,
// the pen is a toolbar above it, and the findings are a list beside it that
// points INTO the image.
//
// ── Three layers, one frame ────────────────────────────────────────────────
// The image, the dentist's marks, and the model's findings are three stacked
// absolutely-positioned layers inside ONE element whose size the image decides.
// They are stacked rather than drawn into a canvas because a mark has to be
// clickable, focusable and describable to a screen reader — a bitmap is none of
// those — and because a canvas would have to be re-rastered on every zoom.
//
// ── Everything is a PERCENTAGE ─────────────────────────────────────────────
// Not one pixel coordinate is stored or drawn. The SVG layers use a fixed
// `viewBox="0 0 100 100"` with `preserveAspectRatio="none"`, so the unit square
// the server stores maps onto whatever box the image currently occupies —
// zoomed, resized, on a phone — with no arithmetic at draw time and no
// listener on the element's size. See lib/imageMarks.js.
//
// ── Zoom is CSS, not layout ────────────────────────────────────────────────
// `transform: scale()` on the frame, with the layers inside it. That keeps the
// three layers in exact register at every zoom level for free: they are one
// subtree, so they scale together and cannot drift by a rounding error.
//
// ── Contrast is a filter, and it is not an edit ────────────────────────────
// `filter: brightness() contrast()` on the <img> ALONE, never on the frame:
// pushing the contrast on a dark periapical is how it gets read, and if the
// filter applied to the whole subtree it would wash out the marks and the
// hotspots too. Nothing here writes a modified image anywhere — the bytes in
// storage are the bytes the sensor produced, and a "brightened" radiograph
// saved over the original is a diagnostic record nobody can re-read.

const TOOL_CURSOR = 'cursor';

const TOOLS = [
  { id: TOOL_CURSOR, icon: MousePointer2 },
  { id: MARK_PIN, icon: MapPin },
  { id: MARK_BOX, icon: Square },
  { id: MARK_ELLIPSE, icon: Circle },
  { id: MARK_ARROW, icon: MoveUpRight },
  { id: MARK_FREEHAND, icon: PenLine },
];

const ZOOM_STEPS = [1, 1.5, 2, 3, 4];

// ── The dentist's marks ─────────────────────────────────────────────────────

function MarkShape({ mark, selected, onSelect, interactive, label }) {
  const stroke = mark.color || DEFAULT_MARK_COLOR;
  // Stroke width is in the 0-100 viewBox, so it thins as the image is zoomed —
  // which is what a magnifying glass does and what a dentist expects. A
  // `vector-effect` that kept it constant would fatten every mark on a phone.
  const width = selected ? 1.4 : 0.9;
  const common = {
    stroke,
    strokeWidth: width,
    fill: 'none',
    vectorEffect: 'non-scaling-stroke',
    style: { cursor: interactive ? 'pointer' : 'default' },
    onPointerDown: interactive ? (e) => { e.stopPropagation(); onSelect(mark.id); } : undefined,
    role: interactive ? 'button' : undefined,
    tabIndex: interactive ? 0 : undefined,
    'aria-label': label,
    onKeyDown: interactive
      ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(mark.id); } }
      : undefined,
  };

  if (mark.kind === MARK_BOX) {
    return (
      <rect
        x={mark.x * 100} y={mark.y * 100}
        width={(mark.w || 0) * 100} height={(mark.h || 0) * 100}
        {...common}
      />
    );
  }
  if (mark.kind === MARK_ELLIPSE) {
    return (
      <ellipse
        cx={(mark.x + (mark.w || 0) / 2) * 100}
        cy={(mark.y + (mark.h || 0) / 2) * 100}
        rx={((mark.w || 0) / 2) * 100}
        ry={((mark.h || 0) / 2) * 100}
        {...common}
      />
    );
  }
  if (mark.kind === MARK_ARROW) {
    return (
      <g {...common}>
        <line
          x1={mark.x * 100} y1={mark.y * 100}
          x2={(mark.x2 ?? mark.x) * 100} y2={(mark.y2 ?? mark.y) * 100}
          stroke={stroke} strokeWidth={width} vectorEffect="non-scaling-stroke"
        />
        {/* The head, drawn as a dot rather than as a rotated triangle: the
            viewBox is not square (preserveAspectRatio="none"), so an angle
            computed in unit space renders skewed on any non-square image. */}
        <circle
          cx={(mark.x2 ?? mark.x) * 100} cy={(mark.y2 ?? mark.y) * 100}
          r={1.1} fill={stroke} stroke="none"
        />
      </g>
    );
  }
  if (mark.kind === MARK_FREEHAND) {
    return (
      <polyline
        points={strokePoints(mark.points)}
        strokeLinecap="round" strokeLinejoin="round"
        {...common}
      />
    );
  }
  // A pin. Two circles rather than a map-pin glyph, because a glyph has a tip
  // and a body and the dentist means the tip — an outline ring centred on the
  // point says exactly where it is.
  return (
    <g {...common}>
      <circle cx={mark.x * 100} cy={mark.y * 100} r={selected ? 2.2 : 1.7}
              stroke={stroke} strokeWidth={width} fill="none"
              vectorEffect="non-scaling-stroke" />
      <circle cx={mark.x * 100} cy={mark.y * 100} r={0.5} fill={stroke} stroke="none" />
    </g>
  );
}

// ── The model's findings ────────────────────────────────────────────────────

function FindingHotspot({ finding, active, onHover, onLeave, onSelect, label }) {
  const tone = confidenceTone(finding.confidence);
  return (
    <g
      onPointerEnter={() => onHover(finding.id)}
      onPointerLeave={onLeave}
      onFocus={() => onHover(finding.id)}
      onBlur={onLeave}
      onPointerDown={(e) => { e.stopPropagation(); onSelect(finding.id); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(finding.id); }
      }}
      // Focusable, so the findings are reachable without a mouse. A hover-only
      // hotspot is a finding a keyboard user cannot discover exists.
      tabIndex={0}
      role="button"
      aria-label={label}
      style={{ cursor: 'pointer', outline: 'none' }}
    >
      <rect
        x={finding.x * 100} y={finding.y * 100}
        width={finding.w * 100} height={finding.h * 100}
        fill={active ? soft(C.teal, 0.14) : 'transparent'}
        stroke={tone.line}
        strokeWidth={active ? 1.4 : 0.9}
        // Dashed, always: it is what tells a proposal apart from a mark a human
        // drew, at a glance and without a legend.
        strokeDasharray="2 1.6"
        vectorEffect="non-scaling-stroke"
        rx={0.8}
      />
    </g>
  );
}

/** The card that appears under the cursor on a hovered finding. */
function FindingTip({ finding, x, y, containerWidth, t }) {
  if (!finding) return null;
  const WIDTH = 230;
  const OFFSET = 14;
  const flip = x + OFFSET + WIDTH > (containerWidth || Infinity);
  const left = flip ? Math.max(4, x - OFFSET - WIDTH) : x + OFFSET;

  return (
    <div
      role="tooltip"
      style={{
        position: 'absolute', left, top: y + OFFSET, width: WIDTH,
        background: '#0f172a', color: '#e2e8f0', borderRadius: 8,
        padding: '9px 11px', boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
        // Load-bearing: the card follows the cursor and would otherwise sit
        // under it, stealing the hover from the hotspot and flickering.
        pointerEvents: 'none',
        zIndex: 30, textAlign: 'start',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 4 }}>
        {finding.toothFdi && (
          <span style={{ fontSize: 14, fontWeight: 800, color: '#fff' }} {...LTR_TEXT}>
            {finding.toothFdi}
          </span>
        )}
        <span style={{ fontSize: 10.5, color: '#94a3b8' }}>
          {t(`visit.image.confidence.${finding.confidence}`)}
        </span>
      </div>
      <div style={{ fontSize: 12.5, lineHeight: 1.7 }}>{finding.label}</div>
      {finding.evidence && (
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 5, lineHeight: 1.7 }}>
          {finding.evidence}
        </div>
      )}
    </div>
  );
}

// ── The workspace ───────────────────────────────────────────────────────────

export default function ImageWorkspace({
  file, url, loadingUrl, urlFailed,
  editable, busy, compact = false,
  onBack, onSave, onAnalyze, onConfirmFinding, onRegister,
  /**
   * How the film is currently being looked at, owned by the CALLER.
   *
   * Zoom and the brightness/contrast pair live outside this component because
   * the modal that holds it unmounts — confirming a finding closes it and sends
   * the dentist to the tooth editor, and they come back to the same film for the
   * next finding. Re-zooming and re-pushing the contrast every time would make
   * the second finding cost more than the first.
   */
  view, onViewChange,
  /** Told when there are unsaved marks, so the caller can guard the close. */
  onDirtyChange,
  t, formatDate, formatNumber,
}) {
  const frameRef = React.useRef(null);
  const wrapRef = React.useRef(null);

  const [tool, setTool] = React.useState(TOOL_CURSOR);
  const [color, setColor] = React.useState(DEFAULT_MARK_COLOR);
  const zoom = view?.zoom ?? 1;
  const brightness = view?.brightness ?? 100;
  const contrast = view?.contrast ?? 100;
  const setView = (patch) => onViewChange?.({ zoom, brightness, contrast, ...patch });
  const setZoom = (next) => setView({
    zoom: typeof next === 'function' ? next(zoom) : next,
  });
  const setBrightness = (v) => setView({ brightness: v });
  const setContrast = (v) => setView({ contrast: v });

  // The marks being edited. Seeded from the file and NOT kept in sync with it
  // afterwards: this is a draft, and a refetch landing mid-edit must not
  // silently discard what the dentist has drawn since.
  const [marks, setMarks] = React.useState(() => file.annotations || []);
  const [dirty, setDirty] = React.useState(false);
  // Reported upward so the modal can refuse to close on a stray Esc or backdrop
  // click. The marks themselves stay here — this is one boolean, not the canvas.
  React.useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  const [selectedId, setSelectedId] = React.useState(null);
  const [drawing, setDrawing] = React.useState(null);
  const [hovered, setHovered] = React.useState(null);
  const [tipAt, setTipAt] = React.useState({ x: 0, y: 0 });
  const [failure, setFailure] = React.useState(null);
  const [readNote, setReadNote] = React.useState(null);
  const [registerAs, setRegisterAs] = React.useState(file.viewType || 'periapical');

  /**
   * Is this file registered as a dental image?
   *
   * Every file uploaded before this screen existed answers NO, and so does one
   * whose link write soft-failed at upload. Such a file can be LOOKED at — that
   * is the whole point of opening it — but not drawn on and not read: marks
   * have nowhere to live without the link row, and a reading has no view type,
   * which is the field that decides what the model may report at all.
   *
   * The tools are HIDDEN rather than disabled, with one line saying why and the
   * control that fixes it. A row of greyed-out pens explains nothing.
   */
  const registered = !!file.imageLinkId;

  // Re-seed when the FILE changes — opening a different image — but never on a
  // refetch of the same one, which is what the id comparison buys.
  const fileId = file.id;
  React.useEffect(() => {
    setMarks(file.annotations || []);
    setDirty(false);
    setSelectedId(null);
    setRegisterAs(file.viewType || 'periapical');
  }, [fileId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = marks.find((m) => m.id === selectedId) || null;
  const findings = file.aiFindings || [];
  const localized = findings.filter(isLocalized);
  const unlocalized = findings.filter((f) => !isLocalized(f));
  const hoveredFinding = findings.find((f) => f.id === hovered) || null;

  const patch = (id, changes) => {
    setMarks((list) => list.map((m) => (m.id === id ? { ...m, ...changes } : m)));
    setDirty(true);
  };

  const drop = (id) => {
    setMarks((list) => list.filter((m) => m.id !== id));
    setSelectedId(null);
    setDirty(true);
  };

  // ── Drawing ──────────────────────────────────────────────────────────────
  //
  // Pointer events, not mouse events: an iPad chairside is the device this is
  // used on, and `pointerdown` covers the pen, the finger and the mouse with
  // one path. `setPointerCapture` keeps a stroke that leaves the image alive
  // until it is released, instead of ending it at the edge.

  const beginDraw = (event) => {
    if (!editable || tool === TOOL_CURSOR) return;
    if (marks.length >= MAX_MARKS) {
      setFailure(new Error(t('visit.image.tooManyMarks', { max: formatNumber(MAX_MARKS) })));
      return;
    }
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const at = toUnit(event, frameRef.current);
    setFailure(null);
    setSelectedId(null);
    setDrawing({ kind: tool, from: at, to: at, points: [at.x, at.y] });
  };

  const moveDraw = (event) => {
    if (!drawing) return;
    const at = toUnit(event, frameRef.current);
    setDrawing((d) => {
      if (!d) return d;
      if (d.kind !== MARK_FREEHAND) return { ...d, to: at };
      // A stroke that hits the ceiling STOPS GROWING rather than being refused:
      // the server caps the point list, and losing an entire long stroke at
      // save time is a worse answer than a stroke that stopped following.
      if (d.points.length >= MAX_STROKE_POINTS * 2) return { ...d, to: at };
      return { ...d, to: at, points: [...d.points, at.x, at.y] };
    });
  };

  const endDraw = () => {
    if (!drawing) return;
    const { kind, from, to, points } = drawing;
    setDrawing(null);
    if (!isDragMeaningful(kind, from, to)) return;

    const geometry = kind === MARK_FREEHAND
      ? { x: points[0], y: points[1], points }
      : geometryFor(kind, from, to);

    const mark = newMark({
      kind,
      geometry,
      color,
      // The tooth the image is of, when it is of exactly one. A bitewing is of
      // several and a panoramic of none, and guessing which one a mark is about
      // from a coordinate is precisely the inference this app must not make.
      toothFdi: (file.teethFdi || []).length === 1 ? file.teethFdi[0] : null,
    });
    setMarks((list) => [...list, mark]);
    setSelectedId(mark.id);
    setDirty(true);
    // Back to the cursor after one mark. A pen that stays armed turns the next
    // click — usually meant to select what was just drawn — into another mark.
    setTool(TOOL_CURSOR);
  };

  const save = async () => {
    setFailure(null);
    const err = await onSave(marks);
    if (err) setFailure(err);
    else setDirty(false);
  };

  const analyze = async () => {
    setFailure(null);
    setReadNote(null);
    const { result, error } = await onAnalyze();
    if (error) {
      setFailure(error);
      return;
    }
    // "The model read it and saw nothing" is not a failure and must not be
    // rendered as one — but it must be rendered, or the button looks broken.
    if (result && result.status !== 'ok') setReadNote(result.status);
  };

  const filter = `brightness(${brightness}%) contrast(${contrast}%)`;
  const wrapWidth = wrapRef.current?.clientWidth || 0;

  return (
    <div data-testid="image-workspace">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10,
        flexWrap: 'wrap',
      }}>
        {/* «Back» points toward where the reader came from: right in Persian,
            left in English, French and German. A fixed scaleX(-1) pointed right
            in every language. The glyph is drawn for RTL; MIRROR_ICON flips it. */}
        <IconButton
          variant="ghost" size={44}
          icon={<ArrowRight size={16} style={MIRROR_ICON} />}
          label={t('visit.image.back')}
          onClick={onBack}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 13.5, fontWeight: 700, color: C.textPrimary,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {file.fileName}
          </div>
          <div style={{ fontSize: 11, color: C.textMuted }}>
            {file.viewType ? t(`visit.files.viewTypes.${file.viewType}`) : ''}
            {file.createdAt ? ` · ${formatDate(file.createdAt, 'numeric')}` : ''}
          </div>
        </div>
        {(file.teethFdi || []).length > 0 && (
          <span style={{ fontSize: 11.5, color: C.textSecondary }} {...LTR_TEXT}>
            {file.teethFdi.join(', ')}
          </span>
        )}
      </div>

      {failure && (
        <Alert variant="error" style={{ marginBottom: 10 }}>
          {failure.message || t('visit.image.saveFailed')}
        </Alert>
      )}
      {readNote && (
        <Alert variant="info" style={{ marginBottom: 10 }}>
          {t(`visit.image.readStatus.${readNote}`)}
        </Alert>
      )}

      {/* ── What this image IS ──────────────────────────────────────────
          Shown whether or not the file is already registered, and that is the
          point: the view type decides what a reading may report at all, and it
          is the field most likely to be wrong. A panoramic mislabelled as a
          periapical comes back with NO findings — the prompt tells the model a
          PA shows two to four teeth and to report only what that view supports
          — and until this could be corrected, the only remedy was deleting the
          file and uploading it again.

          `linkToothImage` is idempotent on (patient_id, ref), so re-registering
          UPDATES the row rather than making a second one; the marks and the
          findings on it are untouched. */}
      {editable && (
        <div style={{
          display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap',
          padding: '10px 12px', marginBottom: 10,
          border: `1px solid ${C.border}`, borderRadius: 10, background: CT.surface2,
        }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 12.5, fontWeight: 700, color: C.textPrimary, marginBottom: 2,
            }}>
              <Tag size={13} aria-hidden />
              {registered
                ? t('visit.image.viewType.title')
                : t('visit.image.notRegistered.title')}
            </div>
            <div style={{ fontSize: 11.5, color: C.textMuted, lineHeight: 1.7 }}>
              {registered
                ? t('visit.image.viewType.body')
                : t('visit.image.notRegistered.body')}
            </div>
          </div>
          <Select
            label={t('visit.files.viewType')}
            aria-label={t('visit.files.viewType')}
            placeholder=""
            value={registerAs}
            onChange={(e) => setRegisterAs(e.target.value)}
            options={VIEW_TYPES.map((v) => ({
              value: v, label: t(`visit.files.viewTypes.${v}`),
            }))}
          />
          {/* On a registered image the button appears only once the dropdown
              actually differs from what is stored — a permanent «ذخیره» beside
              an unchanged value reads as something left undone. */}
          {(!registered || registerAs !== file.viewType) && (
            <Button
              className="min-h-11 flex-none"
              disabled={busy}
              onClick={async () => {
                setFailure(null);
                const err = await onRegister(registerAs);
                if (err) setFailure(err);
              }}
            >
              {busy
                ? t('visit.image.registering')
                : (registered ? t('visit.image.changeView') : t('visit.image.register'))}
            </Button>
          )}
        </div>
      )}

      {/* ── Toolbar ────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        padding: '8px 10px', marginBottom: 8,
        border: `1px solid ${C.border}`, borderRadius: 10, background: CT.surface2,
      }}>
        {editable && registered && (
          <>
            <ToggleGroup
              value={tool}
              onChange={setTool}
              aria-label={t('visit.image.tools')}
              options={TOOLS.map(({ id, icon: Icon }) => ({
                value: id,
                label: <Icon size={15} aria-hidden />,
                'aria-label': t(`visit.image.tool.${id}`),
                title: t(`visit.image.tool.${id}`),
              }))}
            />
            <div style={{ display: 'flex', gap: 4 }} role="group" aria-label={t('visit.image.color')}>
              {MARK_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  aria-label={t(`visit.image.colorName.${c.replace('#', '')}`)}
                  aria-pressed={color === c}
                  style={{
                    width: 22, height: 22, borderRadius: 6, background: c,
                    border: color === c ? `2px solid ${C.textPrimary}` : `1px solid ${C.border}`,
                    cursor: 'pointer', padding: 0,
                  }}
                />
              ))}
            </div>
            <span style={{ width: 1, height: 22, background: C.border }} aria-hidden />
          </>
        )}

        <IconButton
          variant="ghost" size={38} icon={<ZoomOut size={15} />}
          label={t('visit.image.zoomOut')}
          onClick={() => setZoom((z) => ZOOM_STEPS[Math.max(0, ZOOM_STEPS.indexOf(z) - 1)] || 1)}
        />
        <span style={{ fontSize: 11.5, color: C.textSecondary, minWidth: 34, textAlign: 'center' }}>
          {formatNumber(Math.round(zoom * 100))}%
        </span>
        <IconButton
          variant="ghost" size={38} icon={<ZoomIn size={15} />}
          label={t('visit.image.zoomIn')}
          onClick={() => setZoom((z) => ZOOM_STEPS[Math.min(ZOOM_STEPS.length - 1, ZOOM_STEPS.indexOf(z) + 1)] || 1)}
        />
        <IconButton
          variant="ghost" size={38} icon={<Maximize2 size={15} />}
          label={t('visit.image.fit')}
          // ONE patch, not three: each setter derives from the CURRENT view, so
          // three calls in a row would each overwrite the last from stale values.
          onClick={() => onViewChange?.({ zoom: 1, brightness: 100, contrast: 100 })}
        />

        <span style={{ width: 1, height: 22, background: C.border }} aria-hidden />

        {/* Brightness and contrast are how a radiograph is READ, not a filter
            for taste — a dark periapical is unreadable until the contrast is
            pushed. They are view-only and never written back to the file. */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <Sun size={14} aria-hidden color={C.textMuted} />
          <span className="sr-only">{t('visit.image.brightness')}</span>
          <input
            type="range" min={40} max={200} value={brightness}
            aria-label={t('visit.image.brightness')}
            onChange={(e) => setBrightness(Number(e.target.value))}
            style={{ width: compact ? 66 : 84 }}
          />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <Contrast size={14} aria-hidden color={C.textMuted} />
          <span className="sr-only">{t('visit.image.contrast')}</span>
          <input
            type="range" min={40} max={260} value={contrast}
            aria-label={t('visit.image.contrast')}
            onChange={(e) => setContrast(Number(e.target.value))}
            style={{ width: compact ? 66 : 84 }}
          />
        </label>

        <div style={{ flex: 1 }} />

        {editable && registered && (
          <Button
            variant="outline" className="min-h-11"
            onClick={analyze}
            disabled={busy}
          >
            <Sparkles size={14} aria-hidden style={{ marginInlineEnd: 5 }} />
            {busy ? t('visit.image.reading') : t('visit.image.read')}
          </Button>
        )}
        {editable && registered && (
          <Button className="min-h-11" onClick={save} disabled={!dirty || busy}>
            {t('common.actions.save')}
          </Button>
        )}
      </div>

      {/* ── THE SPLIT ───────────────────────────────────────────────────
          The film on one side, everything said ABOUT it on the other.

          The findings used to sit under the image, so hovering a hotspot and
          reading its card meant looking at two places a screen apart — the one
          thing the hover exists to connect. Side by side they are one glance.

          `minWidth: 0` on the image column is load-bearing: a flex child
          defaults to `min-width: auto`, which refuses to shrink below its
          content, and a zoomed film would push the findings column off the
          modal instead of scrolling inside its own box.

          It collapses to one column when there is no room — `compact` is the
          phone, where the modal is nearly full-screen and stacking is right. */}
      <div style={{
        display: 'flex',
        flexDirection: compact ? 'column' : 'row',
        alignItems: 'stretch',
        gap: 12,
      }}>
      <div style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <div
        ref={wrapRef}
        style={{
          position: 'relative', overflow: 'auto',
          border: `1px solid ${C.border}`, borderRadius: 10,
          background: '#0b1220',
          minHeight: compact ? 220 : 320,
          maxHeight: compact ? 'none' : '70vh',
          // `display:flex` with `justify-content:center` was here and had to go:
          // centring an OVERFLOWING flex child makes its start edge unreachable
          // — the scroll range begins at the overflow, so the left of a zoomed
          // film could not be scrolled to at all. `margin:auto` on the frame
          // below centres it while there is room and stops centring when there
          // is not, which is exactly the behaviour a zoom needs.
        }}
        onPointerMove={(e) => {
          const rect = wrapRef.current?.getBoundingClientRect();
          if (rect) setTipAt({ x: e.clientX - rect.left, y: e.clientY - rect.top });
        }}
      >
        {/* The film's own footprint while its bytes are fetched — one block the
            size the canvas reserves — not a spinner in a dark box (DESIGN.md
            §"Loading & skeleton"). `tone="onDark"`, because the kit's neutral
            grey reads as a hole on the viewer's dark ground. */}
        {loadingUrl && (
          <SkeletonRegion label={t('common.loading')} style={{ padding: 12 }}>
            <Skeleton
              width="100%"
              height={compact ? 196 : 296}
              radius={8}
              tone="onDark"
            />
          </SkeletonRegion>
        )}
        {!loadingUrl && urlFailed && (
          <div style={{ padding: 40, fontSize: 12.5, color: '#94a3b8' }}>
            {file.storageFileId ? t('visit.files.previewFailed') : t('visit.files.bytesGone')}
          </div>
        )}
        {!loadingUrl && url && (
          <div
            ref={frameRef}
            style={{
              position: 'relative',
              // ── ZOOM IS LAYOUT, NOT A TRANSFORM ──────────────────────────
              // This was `transform: scale(zoom)`, and a transform does not
              // change layout size: the scroll area of the box above stayed at
              // 1× however far the film was zoomed, so past ~2× the edges could
              // not be reached at all. Widening the FRAME makes the overflow
              // real, and the scrollbars mean it.
              //
              // The three layers stay in exact register for free either way —
              // the image and both SVGs are children of this element and are
              // sized from it (`maxWidth:100%` and `inset:0`), so they all
              // follow one number. That was the property the transform was
              // chosen for, and it survives the change.
              width: `${zoom * 100}%`,
              margin: 'auto',
              lineHeight: 0,
              touchAction: drawing ? 'none' : 'auto',
              cursor: editable && registered && tool !== TOOL_CURSOR ? 'crosshair' : 'default',
            }}
            onPointerDown={beginDraw}
            onPointerMove={moveDraw}
            onPointerUp={endDraw}
            onPointerCancel={endDraw}
          >
            <img
              src={url}
              alt={file.fileName}
              draggable={false}
              style={{
                // `width:100%` and not `maxWidth`: the frame is what the zoom
                // widens, and the image has to follow it. `maxWidth` would cap
                // the film at its natural pixel size and the zoom would stop
                // having any effect once it passed that.
                display: 'block', width: '100%', height: 'auto',
                // ONLY the image. Applied to the frame it would wash out the
                // marks and the hotspots along with the film.
                filter,
                userSelect: 'none',
              }}
            />

            {/* The dentist's marks. */}
            <svg
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              style={{
                position: 'absolute', inset: 0, width: '100%', height: '100%',
                // Marks are clickable; the empty space between them is not, so
                // a drag that starts on background still reaches the frame.
                pointerEvents: editable && registered ? 'auto' : 'none',
              }}
              aria-hidden={marks.length === 0}
            >
              {marks.map((m) => (
                <MarkShape
                  key={m.id}
                  mark={m}
                  selected={m.id === selectedId}
                  interactive={editable && tool === TOOL_CURSOR}
                  onSelect={setSelectedId}
                  label={m.comment || t(`visit.image.tool.${m.kind}`)}
                />
              ))}
              {drawing && (
                <MarkShape
                  mark={{
                    kind: drawing.kind,
                    color,
                    points: drawing.points,
                    ...(drawing.kind === MARK_FREEHAND
                      ? { x: drawing.points[0], y: drawing.points[1] }
                      : geometryFor(drawing.kind, drawing.from, drawing.to)),
                  }}
                  selected
                  interactive={false}
                  onSelect={() => {}}
                  label=""
                />
              )}
            </svg>

            {/* The model's findings, above the marks so a hotspot is reachable
                even where the two overlap — the finding is the thing being
                asked about, and the mark is already selectable from its own
                layer with the cursor tool. */}
            <svg
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              style={{
                position: 'absolute', inset: 0, width: '100%', height: '100%',
                pointerEvents: localized.length ? 'auto' : 'none',
              }}
            >
              {localized.map((f) => (
                <FindingHotspot
                  key={f.id}
                  finding={f}
                  active={hovered === f.id}
                  onHover={setHovered}
                  onLeave={() => setHovered(null)}
                  onSelect={setHovered}
                  label={f.label}
                />
              ))}
            </svg>
          </div>
        )}

        <FindingTip
          finding={hoveredFinding}
          x={tipAt.x} y={tipAt.y}
          containerWidth={wrapWidth}
          t={t}
        />
      </div>

      {/* ── The selected mark's comment ────────────────────────────────── */}
      {editable && registered && selected && (
        <div style={{
          marginTop: 10, padding: 10,
          border: `1px solid ${C.border}`, borderRadius: 10, background: CT.surface,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{
              width: 12, height: 12, borderRadius: 3,
              background: selected.color || DEFAULT_MARK_COLOR,
            }} aria-hidden />
            <span style={{ fontSize: 12.5, fontWeight: 700, color: C.textPrimary }}>
              {t(`visit.image.tool.${selected.kind}`)}
            </span>
            {selected.toothFdi && (
              <Badge variant="outline"><span {...LTR_TEXT}>{selected.toothFdi}</span></Badge>
            )}
            <div style={{ flex: 1 }} />
            <IconButton
              variant="ghost" size={38} icon={<Trash2 size={14} />}
              label={t('visit.image.removeMark')}
              onClick={() => drop(selected.id)}
            />
          </div>
          <Textarea
            value={selected.comment || ''}
            onChange={(e) => patch(selected.id, {
              comment: e.target.value.slice(0, MAX_COMMENT_CHARS),
            })}
            placeholder={t('visit.image.commentPlaceholder')}
            aria-label={t('visit.image.comment')}
            rows={2}
          />
        </div>
      )}

      </div>{/* ── end of the image column ── */}

      {/* ── What the model read ─────────────────────────────────────────
          A fixed-width column beside the film, not a panel under it. It scrolls
          on its own so a long list never pushes the image out of view — the
          image is the thing being read, and it does not move.

          Absent entirely until something has been read: an empty 300px gutter
          beside the film is space taken from the thing being looked at, to say
          nothing at all. */}
      {findings.length > 0 && (
      <div style={{
        flex: compact ? '1 1 auto' : '0 0 300px',
        minWidth: 0,
        maxHeight: compact ? 'none' : '70vh',
        overflowY: compact ? 'visible' : 'auto',
      }}>
        <div>
          <div style={{
            display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6,
          }}>
            <span style={{ fontSize: 12.5, fontWeight: 800, color: C.textPrimary }}>
              {t('visit.image.findings')}
            </span>
            <span style={{ fontSize: 11, color: C.textMuted }}>
              {t('visit.image.findingsNote')}
            </span>
          </div>

          {findings.map((f) => {
            const tone = confidenceTone(f.confidence);
            const active = hovered === f.id;
            return (
              <div
                key={f.id}
                onPointerEnter={() => setHovered(f.id)}
                onPointerLeave={() => setHovered(null)}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 9,
                  padding: '8px 10px', marginBottom: 6,
                  border: `1px solid ${active ? tone.line : C.border}`,
                  borderRadius: 9,
                  background: active ? CT.surface2 : CT.surface,
                }}
              >
                <span style={{
                  width: 8, height: 8, borderRadius: 2, marginTop: 5,
                  background: tone.line, flex: 'none',
                }} aria-hidden />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, color: C.textPrimary, lineHeight: 1.7 }}>
                    {f.toothFdi && (
                      <span style={{ fontWeight: 800, marginInlineEnd: 5 }} {...LTR_TEXT}>
                        {f.toothFdi}
                      </span>
                    )}
                    {f.label}
                  </div>
                  {f.evidence && (
                    <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
                      {f.evidence}
                    </div>
                  )}
                  <div style={{ fontSize: 10.5, color: tone.ink, marginTop: 3 }}>
                    {t(`visit.image.confidence.${f.confidence}`)}
                    {!isLocalized(f) && ` · ${t('visit.image.notLocalized')}`}
                  </div>
                </div>

                {/* The tick is HIDDEN, not disabled, on a descriptive finding:
                    there is no tooth event to write, and an always-refused
                    control is worse than none. */}
                {editable && isConfirmable(f) && (
                  <Button
                    variant="outline"
                    className="min-h-11 flex-none"
                    onClick={() => onConfirmFinding(f)}
                  >
                    <Check size={13} aria-hidden style={{ marginInlineEnd: 4 }} />
                    {t('visit.image.confirm')}
                  </Button>
                )}
              </div>
            );
          })}

          {unlocalized.length > 0 && (
            <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
              {t('visit.image.unlocalizedNote', {
                count: formatNumber(unlocalized.length),
              })}
            </div>
          )}
        </div>
      </div>
      )}{/* ── end of the findings column ── */}
      </div>{/* ── end of the split ── */}

      {editable && registered && dirty && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginTop: 10,
          fontSize: 11.5, color: C.textMuted,
        }}>
          <RotateCcw size={13} aria-hidden />
          {t('visit.image.unsaved')}
        </div>
      )}
    </div>
  );
}
